import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, CalendarDays, Link2, Blocks, BookA, Lightbulb, History, Calculator, KeyRound } from "lucide-react";
import { OnThisDay } from "@/components/OnThisDay";
import { IdeaBridge } from "@/components/IdeaBridge";
import { AnalogyMachine } from "@/components/AnalogyMachine";
import { Etymology } from "@/components/Etymology";
import { DidYouKnow } from "@/components/DidYouKnow";
import { TimelineTool } from "@/components/TimelineTool";
import { CalculMental } from "@/components/CalculMental";
import { Cabinet } from "@/components/Cabinet";
import { CinematicVideo } from "@/components/CinematicVideo";

const CURIOSITY_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/42mliap5_gemini_generated_video_9308ad94.mp4";

const TOOLS = [
  { key: "onthisday", icon: CalendarDays, label: "tabOnThisDay", Comp: OnThisDay, wrap: true },
  { key: "bridge", icon: Link2, label: "tabBridge", Comp: IdeaBridge, wrap: true },
  { key: "analogy", icon: Blocks, label: "tabAnalogy", Comp: AnalogyMachine, wrap: true },
  { key: "etymology", icon: BookA, label: "tabEtymology", Comp: Etymology, wrap: false },
  { key: "didyouknow", icon: Lightbulb, label: "tabDidYouKnow", Comp: DidYouKnow, wrap: false },
  { key: "timeline", icon: History, label: "tabTimeline", Comp: TimelineTool, wrap: false },
  { key: "calcul", icon: Calculator, label: "tabCalcul", Comp: CalculMental, wrap: true },
  { key: "cabinet", icon: KeyRound, label: "tabCabinet", Comp: Cabinet, wrap: true },
];

export function CuriosityHub() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const tool = TOOLS[active];
  const Current = tool.Comp;

  return (
    <section className="relative overflow-hidden" data-testid="curiosity-hub-section">
      <div className="amber-glow" aria-hidden style={{ top: "6%", left: "50%", transform: "translateX(-50%)", width: "820px", height: "560px", opacity: 0.85 }} />
      <div className="max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-2 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-9"
        >
          <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
            <Sparkles size={14} /> {t("landing.curiosityHub.eyebrow")}
          </p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight mb-4" data-testid="curiosity-hub-title">
            {t("landing.curiosityHub.title")}
          </h2>
          <p className="text-foreground/55 italic max-w-md mx-auto">{t("landing.curiosityHub.subtitle")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="max-w-2xl mx-auto mb-9"
        >
          <CinematicVideo fade noSound silver src={CURIOSITY_VIDEO} testId="curiosity-cinematic-video" caption={t("landing.curiosityHub.title")} />
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mb-2" role="tablist" data-testid="curiosity-tablist">
          {TOOLS.map((tl, i) => {
            const Icon = tl.icon;
            const on = active === i;
            return (
              <motion.button
                key={tl.key}
                onClick={() => setActive(i)}
                data-testid={`curiosity-tab-${tl.key}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.96 }}
                aria-selected={on}
                role="tab"
                className={`group card-sheen relative flex flex-col items-center justify-center gap-2.5 h-24 sm:h-28 rounded-2xl border px-2.5 text-center overflow-hidden transition-colors duration-300 ${
                  on
                    ? "border-primary/50 bg-primary/[0.08] text-primary shadow-[0_12px_44px_-14px_hsl(28_96%_56%_/_0.6)]"
                    : "border-white/10 bg-white/[0.02] text-foreground/60 hover:border-primary/30 hover:text-primary/90"
                }`}
              >
                {on && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{ background: "radial-gradient(circle at 50% 28%, hsl(28 96% 56% / 0.18), transparent 68%)" }}
                  />
                )}
                <span
                  className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-300 ${
                    on ? "border-primary/50 bg-primary/15" : "border-white/10 bg-white/[0.03] group-hover:border-primary/30"
                  }`}
                >
                  <Icon size={18} strokeWidth={1.7} className={on ? "pulse-star" : ""} />
                </span>
                <span className="relative z-10 text-[11px] sm:text-xs font-medium leading-tight line-clamp-2">
                  {t(`landing.curiosityHub.${tl.label}`)}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tool.key}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
        >
          {tool.wrap ? (
            <section className="max-w-4xl mx-auto px-6 pt-8 pb-6">
              <div className="glass stats-wow rounded-[28px] border border-primary/15 px-5 sm:px-9 py-9 sm:py-12 relative overflow-hidden">
                <Current />
              </div>
            </section>
          ) : (
            <div className="pt-8 pb-4">
              <Current />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
